import { supabase } from '../config/supabaseClient';
import { normalizePartNumber } from '../utils/partNumber';

/**
 * Client for the parts-widget edge function (probe / search / byMachine /
 * submit) plus the direct anon parts_catalog keyword search.
 *
 * The edge function always returns HTTP 200 and carries structured errors as
 * { code } in the body (the public-form-verify convention); those surface
 * here as PartsOrderApiError so callers can branch on err.code
 * (verification_required -> bounce to the verify step, already_submitted ->
 * treat as success with the returned order number).
 */

export interface WidgetBranchStock {
  location_id: number;
  location_name: string;
  on_hand: number;
}

export interface WidgetAccount {
  id: number;
  display_name: string;
}

export interface WidgetSearchResult {
  found: boolean;
  part_number: string;
  description: string | null;
  list_price: number | null;
  discount_group: string | null;
  customer_unit_price: number | null;
  install_unit_price: number | null;
  quantity_across_all: number;
  branches: WidgetBranchStock[];
  /** Present only when the verified phone belongs to more than one account. */
  accounts?: WidgetAccount[];
}

export interface SubmitOrderItem {
  part_number: string;
  quantity: number;
}

export interface SubmittedOrderLine {
  part_number: string;
  description: string | null;
  quantity_ordered: number;
  unit_price: number | null;
  customer_unit_price: number | null;
  availability_status: string;
}

export type SubmitOrderResult =
  | {
      status: 'order';
      order_number: string;
      estimated_ready_at: string | null;
      availability_category: string;
      location_name: string;
      items: SubmittedOrderLine[];
    }
  | { status: 'lead' };

// Cart display state (superset of what submit sends: part_number + quantity).
// Displayed price = customer_unit_price ?? unit_price. Keyed by part_number.
export interface PartsOrderCartItem {
  part_number: string;
  description: string | null;
  quantity: number;
  unit_price: number | null;
  customer_unit_price?: number | null;
  install_unit_price?: number | null;
  discount_group?: string | null;
  quantity_across_all: number;
  branches: WidgetBranchStock[];
  availability_status: string;
  image_url?: string | null;
}

export function toCartItem(res: WidgetSearchResult, imageUrl?: string | null): PartsOrderCartItem {
  return {
    part_number: normalizePartNumber(res.part_number),
    description: res.description ?? null,
    quantity: 1,
    unit_price: res.list_price,
    customer_unit_price: res.customer_unit_price,
    install_unit_price: res.install_unit_price,
    discount_group: res.discount_group,
    quantity_across_all: res.quantity_across_all,
    branches: res.branches,
    availability_status: res.quantity_across_all > 0 ? 'in_stock' : 'vendor_order_needed',
    image_url: imageUrl ?? null,
  };
}

// Mirrors pfwGateway compute_item_availability so the review step previews
// the same status the submitted order will carry for the chosen location.
export function availabilityAt(
  item: PartsOrderCartItem,
  locationId: number,
): 'in_stock' | 'transfer_needed' | 'vendor_order_needed' {
  const onHand = item.branches.find((b) => b.location_id === locationId)?.on_hand ?? 0;
  if (onHand >= item.quantity) return 'in_stock';
  if (item.quantity_across_all >= item.quantity) return 'transfer_needed';
  return 'vendor_order_needed';
}

// Backend caps submit at 20 items, qty 1-999 each.
export const MAX_CART_ITEMS = 20;
export const MAX_ITEM_QUANTITY = 999;

export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const displayPrice = (item: PartsOrderCartItem): number | null =>
  item.customer_unit_price ?? item.unit_price;

export const cartTotal = (items: PartsOrderCartItem[]): number | null => {
  const priced = items.filter((i) => displayPrice(i) != null);
  if (priced.length === 0) return null;
  return priced.reduce((sum, i) => sum + (displayPrice(i) ?? 0) * i.quantity, 0);
};

export const ERROR_COPY: Record<string, string> = {
  verification_required: 'Your verification expired. Please verify your phone or email again.',
  already_submitted: 'This order was already submitted.',
  pfw_not_configured: 'Online parts ordering is not available right now.',
  part_not_found: "We couldn't find that part number. Double-check it and try again.",
  rate_limited: 'Too many requests. Please wait a minute and try again.',
  invalid_location: 'Please choose a pickup location and try again.',
  invalid_items: "Something's wrong with your cart. Remove and re-add the items.",
  org_mismatch: 'Something went wrong. Please refresh and try again.',
  lookup_failed: "We couldn't check parts availability right now. Please try again.",
  search_failed: "We couldn't check parts availability right now. Please try again.",
  submit_failed: "We couldn't submit your order. Please try again.",
  invalid_account: 'That account is not available for this order.',
  org_not_found: 'Organization not found.',
};

export function copyFor(code: string | undefined): string {
  return (code && ERROR_COPY[code]) || 'Something went wrong. Please try again.';
}

export class PartsOrderApiError extends Error {
  code: string;
  orderNumber: string | null;

  constructor(code: string, orderNumber?: string | null) {
    super(copyFor(code));
    this.code = code;
    this.orderNumber = orderNumber ?? null;
  }
}

async function invoke<T>(organizationId: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('parts-widget', {
    body: { ...body, organizationId },
  });
  if (error) {
    throw new PartsOrderApiError('request_failed');
  }
  if (data?.code) {
    throw new PartsOrderApiError(data.code, data.order_number ?? null);
  }
  return data as T;
}

export function probe(organizationId: string): Promise<{ available: boolean }> {
  return invoke(organizationId, { action: 'probe' });
}

// `session`: the machine hub's session secret; null on the standalone widget, whose own verify flow mints none.
export function searchPartNumber(
  organizationId: string,
  codeId: number,
  partNumber: string,
  session: string | null = null,
): Promise<WidgetSearchResult> {
  return invoke(organizationId, { action: 'search', codeId, partNumber, ...(session ? { session } : {}) });
}

export function submitOrder(
  organizationId: string,
  codeId: number,
  locationId: number,
  items: SubmitOrderItem[],
  extras: { name?: string; email?: string; phone?: string; notes?: string; customerId?: number } = {},
  session: string | null = null,
): Promise<SubmitOrderResult> {
  return invoke(organizationId, { action: 'submit', codeId, locationId, items, ...extras, ...(session ? { session } : {}) });
}

// Best-effort thumbnail for a live PFW search hit (PFW returns no images);
// anon SELECT on the global parts_catalog is allowed.
export async function catalogImageFor(partNumber: string): Promise<string | null> {
  const { data } = await supabase
    .from('parts_catalog')
    .select('image_url')
    .eq('part_number', partNumber)
    .not('image_url', 'is', null)
    .limit(1);
  return data?.[0]?.image_url ?? null;
}
